// V3 — distribute a (discipline × channel) total FTE into per-level cells via levelWeights.

import type {
  ClientProfile,
  LevelId,
  LevelMixOverride,
  LevelWeightRow,
  Model,
} from '../types.js';
import { LEVEL_IDS } from '../types.js';
import { lookupLevelWeights } from './table-lookup.js';
import { clampShift, tiltWeights } from './level-buckets.js';
import { LEVEL_GROUPS } from './level-groups.js';
import type { LevelGroupId } from './level-groups.js';

/** Sum & normalize a weights row to guard against rows that don't quite sum to 1. */
export function normalizedWeights(row: LevelWeightRow | undefined): Partial<Record<LevelId, number>> {
  if (!row) return {};
  const entries = Object.entries(row.weights || {}) as [LevelId, number][];
  const sum = entries.reduce((s, [, v]) => s + (Number(v) || 0), 0);
  if (sum <= 1e-9) return {};
  const out: Partial<Record<LevelId, number>> = {};
  for (const [k, v] of entries) {
    out[k] = (Number(v) || 0) / sum;
  }
  return out;
}

/** Distribute total FTE across L1–L8 via the given level-weights row. */
export function distributeByLevel(
  totalFte: number,
  weightsRow: LevelWeightRow | undefined,
): Partial<Record<LevelId, number>> {
  const out: Partial<Record<LevelId, number>> = {};
  if (totalFte <= 0) return out;
  const w = normalizedWeights(weightsRow);
  for (const lvl of LEVEL_IDS) {
    const share = Number(w[lvl]) || 0;
    if (share > 0) out[lvl] = totalFte * share;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Leveling — scenario-driven seniority tilt
// ---------------------------------------------------------------------------

function sameChannel(a: string | undefined, b: string | undefined): boolean {
  return (a ?? 'all') === (b ?? 'all');
}

/** Resolve the seniority shift for a (discipline, channel): exact channel
 *  match first, then a discipline-wide (`'all'`) entry. 0 when none. */
export function resolveLevelShift(
  profile: ClientProfile,
  disciplineId: string,
  channelId: string,
): number {
  const list: LevelMixOverride[] = profile.levelMix || [];
  const forDiscipline = list.filter((e) => e.disciplineId === disciplineId);
  const exact = forDiscipline.find((e) => sameChannel(e.channelId, channelId));
  const wide = forDiscipline.find((e) => sameChannel(e.channelId, 'all'));
  return clampShift((exact ?? wide)?.shift);
}

/** Base level-weights row with any scenario seniority tilt applied. Returns
 *  the untouched base row when there is no (non-zero) override. */
export function resolveLevelWeights(
  model: Model,
  profile: ClientProfile,
  disciplineId: string,
  channelId: string,
): LevelWeightRow | undefined {
  const base = lookupLevelWeights(model, disciplineId, channelId);
  const shift = resolveLevelShift(profile, disciplineId, channelId);
  if (!base || shift === 0) return base;
  return { ...base, weights: tiltWeights(base.weights, shift, model.seniorityTiltStrength) };
}

/**
 * Preview the resulting normalized share per level group for a given
 * seniority `shift`, plus which groups are non-empty in the base model (so
 * the UI can dim groups that will always stay 0). Total FTE is unchanged by
 * the tilt; this only reports composition.
 */
export function levelMixPreview(
  model: Model,
  disciplineId: string,
  channelId: string,
  shift: number,
): { groups: Record<LevelGroupId, number>; active: Record<LevelGroupId, boolean> } {
  const base = lookupLevelWeights(model, disciplineId, channelId);
  const tilted = normalizedWeights(
    base
      ? { ...base, weights: tiltWeights(base.weights, shift, model.seniorityTiltStrength) }
      : undefined,
  );
  const groups = {} as Record<LevelGroupId, number>;
  const active = {} as Record<LevelGroupId, boolean>;
  for (const g of LEVEL_GROUPS) {
    let share = 0;
    let baseSum = 0;
    for (const lvl of g.levelIds) {
      share += Number(tilted[lvl]) || 0;
      baseSum += Number(base?.weights?.[lvl]) || 0;
    }
    groups[g.id] = share;
    active[g.id] = baseSum > 0;
  }
  return { groups, active };
}
